/**
 * Recommended lessons — shared translation helpers.
 *
 * All keys are LITERAL strings (literal-i18n-keys rule): the error type slug
 * goes through an exhaustive switch, never template interpolation of the key.
 *
 * @see docs/specs/mobile/practice.mobile.md
 */

import type { TFunction } from 'i18next';
import type { RecommendedLesson } from '../../types/lesson';

/**
 * Human-readable name for a backend error-type slug.
 *
 * Covers the full competence taxonomy the backend now canonicalizes to
 * (`backend/src/services/grammar/grammarPoint.ts`): 62 grammar competences plus
 * the 19 pronunciation / receptive-skill / L1-transfer slugs that other
 * subsystems own but which still key lesson recommendations.
 *
 * The 11 competences ADR-034 added when the CEFR ceiling moved to C1 —
 * `inversion`, `cleft_sentences`, `fronting_emphasis`, `nominalization`,
 * `hedging_stance`, `modal_nuance` and the five points below them — landed here
 * later than the rest, and until they did an advanced learner's recommendation
 * rendered as "Otro". Anything added to `GRAMMAR_POINTS` needs a case here and a
 * key in BOTH locale files, or it silently degrades the same way.
 *
 * This list used to hold only the 10 slugs the OLD analysis prompt enumerated.
 * Once the backend collapsed its 95 drifted slugs onto canonical keys, the single
 * largest bucket — `verb_tense`, which absorbs `tense_usage` / `verb_form` /
 * `verb_forms` / `verb_usage` / `present_tense` — was not among them, so the
 * majority of real recommendations rendered as "Otro". That is the opposite of
 * what the canonicalization was for.
 *
 * Unknown slugs still fall back to "Other" rather than echoing a raw English slug
 * at a Spanish reader — the lesson title carries the specificity.
 */
export function translateErrorType(t: TFunction, errorType: string): string {
  switch (errorType) {
    // Grammar competences (services/grammar/grammarPoint.ts)
    case 'verb_tense': return t('practice.recommended.errorTypes.verb_tense');
    case 'past_tense': return t('practice.recommended.errorTypes.past_tense');
    case 'present_simple': return t('practice.recommended.errorTypes.present_simple');
    case 'present_continuous': return t('practice.recommended.errorTypes.present_continuous');
    case 'present_perfect': return t('practice.recommended.errorTypes.present_perfect');
    case 'future_forms': return t('practice.recommended.errorTypes.future_forms');
    case 'passive_voice': return t('practice.recommended.errorTypes.passive_voice');
    case 'conditionals': return t('practice.recommended.errorTypes.conditionals');
    case 'subjunctive': return t('practice.recommended.errorTypes.subjunctive');
    case 'modal_verbs': return t('practice.recommended.errorTypes.modal_verbs');
    case 'auxiliary_verbs': return t('practice.recommended.errorTypes.auxiliary_verbs');
    case 'gerund_vs_infinitive': return t('practice.recommended.errorTypes.gerund_vs_infinitive');
    case 'reported_speech': return t('practice.recommended.errorTypes.reported_speech');
    case 'verb_complementation': return t('practice.recommended.errorTypes.verb_complementation');
    case 'question_tags': return t('practice.recommended.errorTypes.question_tags');
    case 'phrasal_verbs': return t('practice.recommended.errorTypes.phrasal_verbs');
    case 'question_formation': return t('practice.recommended.errorTypes.question_formation');
    case 'negation': return t('practice.recommended.errorTypes.negation');
    case 'there_is_are': return t('practice.recommended.errorTypes.there_is_are');
    case 'subject_verb_agreement': return t('practice.recommended.errorTypes.subject_verb_agreement');
    case 'modal_nuance': return t('practice.recommended.errorTypes.modal_nuance');
    case 'articles': return t('practice.recommended.errorTypes.articles');
    case 'singular_plural': return t('practice.recommended.errorTypes.singular_plural');
    case 'countable_uncountable': return t('practice.recommended.errorTypes.countable_uncountable');
    case 'quantifiers': return t('practice.recommended.errorTypes.quantifiers');
    case 'possessives': return t('practice.recommended.errorTypes.possessives');
    case 'demonstratives': return t('practice.recommended.errorTypes.demonstratives');
    case 'pronouns': return t('practice.recommended.errorTypes.pronouns');
    case 'subject_pronoun_omission': return t('practice.recommended.errorTypes.subject_pronoun_omission');
    case 'reflexive_pronouns': return t('practice.recommended.errorTypes.reflexive_pronouns');
    case 'gender_confusion': return t('practice.recommended.errorTypes.gender_confusion');
    case 'adjective_order': return t('practice.recommended.errorTypes.adjective_order');
    case 'adjective_adverb': return t('practice.recommended.errorTypes.adjective_adverb');
    case 'adverb_position': return t('practice.recommended.errorTypes.adverb_position');
    case 'comparatives': return t('practice.recommended.errorTypes.comparatives');
    case 'intensifiers': return t('practice.recommended.errorTypes.intensifiers');
    case 'degree_too_very_much': return t('practice.recommended.errorTypes.degree_too_very_much');
    case 'word_order': return t('practice.recommended.errorTypes.word_order');
    case 'sentence_structure': return t('practice.recommended.errorTypes.sentence_structure');
    case 'relative_clauses': return t('practice.recommended.errorTypes.relative_clauses');
    case 'conjunctions': return t('practice.recommended.errorTypes.conjunctions');
    case 'prepositions': return t('practice.recommended.errorTypes.prepositions');
    case 'duration_since_for': return t('practice.recommended.errorTypes.duration_since_for');
    case 'ellipsis': return t('practice.recommended.errorTypes.ellipsis');
    case 'inversion': return t('practice.recommended.errorTypes.inversion');
    case 'cleft_sentences': return t('practice.recommended.errorTypes.cleft_sentences');
    case 'fronting_emphasis': return t('practice.recommended.errorTypes.fronting_emphasis');
    case 'nominalization': return t('practice.recommended.errorTypes.nominalization');
    case 'vocabulary': return t('practice.recommended.errorTypes.vocabulary');
    case 'word_choice': return t('practice.recommended.errorTypes.word_choice');
    case 'false_friends': return t('practice.recommended.errorTypes.false_friends');
    case 'collocations': return t('practice.recommended.errorTypes.collocations');
    case 'spanish_transfer': return t('practice.recommended.errorTypes.spanish_transfer');
    case 'ser_estar': return t('practice.recommended.errorTypes.ser_estar');
    case 'register': return t('practice.recommended.errorTypes.register');
    case 'pragmatics': return t('practice.recommended.errorTypes.pragmatics');
    case 'discourse_markers': return t('practice.recommended.errorTypes.discourse_markers');
    case 'coherence': return t('practice.recommended.errorTypes.coherence');
    case 'hedging_stance': return t('practice.recommended.errorTypes.hedging_stance');
    case 'spelling': return t('practice.recommended.errorTypes.spelling');
    case 'punctuation': return t('practice.recommended.errorTypes.punctuation');
    case 'capitalization': return t('practice.recommended.errorTypes.capitalization');

    // Owned elsewhere (phoneme system / receptive skills) but they key lessons
    case 'pronunciation': return t('practice.recommended.errorTypes.pronunciation');
    case 'pronunciation_consonants': return t('practice.recommended.errorTypes.pronunciation_consonants');
    case 'pronunciation_vowels': return t('practice.recommended.errorTypes.pronunciation_vowels');
    case 'pronunciation_stress': return t('practice.recommended.errorTypes.pronunciation_stress');
    case 'pronunciation_rhythm': return t('practice.recommended.errorTypes.pronunciation_rhythm');
    case 'pronunciation_prosody': return t('practice.recommended.errorTypes.pronunciation_prosody');
    case 'plural_pronunciation': return t('practice.recommended.errorTypes.plural_pronunciation');
    case 'word_stress': return t('practice.recommended.errorTypes.word_stress');
    case 'connected_speech': return t('practice.recommended.errorTypes.connected_speech');
    case 'reduced_forms': return t('practice.recommended.errorTypes.reduced_forms');
    case 'accent_awareness': return t('practice.recommended.errorTypes.accent_awareness');
    case 'listening_comprehension': return t('practice.recommended.errorTypes.listening_comprehension');
    case 'academic_listening': return t('practice.recommended.errorTypes.academic_listening');
    case 'inference': return t('practice.recommended.errorTypes.inference');
    case 'main_idea': return t('practice.recommended.errorTypes.main_idea');
    case 'critical_thinking': return t('practice.recommended.errorTypes.critical_thinking');
    case 'media_literacy': return t('practice.recommended.errorTypes.media_literacy');
    case 'note_taking': return t('practice.recommended.errorTypes.note_taking');
    default: return t('practice.recommended.errorTypes.other');
  }
}

/**
 * The "why am I seeing this" line, e.g. "Past tense · 12 mistakes".
 * This is the motivating half of the card — it ties the lesson to a mistake
 * the learner actually keeps making.
 */
export function formatReasonLine(t: TFunction, item: RecommendedLesson): string {
  return [
    translateErrorType(t, item.errorType),
    t('practice.recommended.mistakeCount', { count: item.errorCount }),
  ].join(' · ');
}

/**
 * Spoken form for VoiceOver/TalkBack, e.g.
 * "Study: Articles in English, Articles, 12 mistakes".
 * Commas rather than the middle dot, which screen readers skip or spell out.
 */
export function recommendationA11yLabel(t: TFunction, item: RecommendedLesson): string {
  return [
    `${t('practice.recommended.itemLabel')}${item.lesson.title}`,
    translateErrorType(t, item.errorType),
    t('practice.recommended.mistakeCount', { count: item.errorCount }),
  ].join(', ');
}

/**
 * Spoken form for the row's secondary action, e.g.
 * "Practicar en conversación: Pronombres de sujeto".
 *
 * Names the COMPETENCE, not the lesson: the two actions on a row lead to
 * different places, and a screen-reader user meets them back to back with
 * nothing but these labels to tell them apart.
 */
export function competenceFocusA11yLabel(t: TFunction, item: RecommendedLesson): string {
  return `${t('practice.recommended.focusLabel')}${translateErrorType(t, item.errorType)}`;
}
